<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="运营端 · 骑手钱包：绑卡审核、提现审核（通过仅为台账确认，非真实银行/微信打款）、钱包查询。"
    />

    <el-tabs v-model="tab">
      <el-tab-pane label="绑卡审核" name="bind">
        <el-button type="primary" class="mb-12px" @click="loadBind">刷新待审</el-button>
        <el-table v-loading="bindLoading" :data="bindList" size="small" border>
          <el-table-column prop="phone" label="手机号" min-width="130" />
          <el-table-column prop="pendingRealName" label="待审实名" min-width="100" />
          <el-table-column prop="pendingCardLast4" label="待审尾号" width="100" />
          <el-table-column label="当前生效卡" min-width="140">
            <template #default="{ row }">
              <span v-if="row.cardLast4">{{ row.realName }} · 尾号 {{ row.cardLast4 }}</span>
              <span v-else class="text-gray-400">无</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template #default="{ row }">
              <el-tag :type="cardStatusTagType(row.cardStatus)" size="small">
                {{ cardStatusLabel(row.cardStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button link type="success" @click="onBindReview(row, true)">通过</el-button>
              <el-button link type="danger" @click="onBindReject(row)">驳回</el-button>
            </template>
          </el-table-column>
        </el-table>
        <Pagination
          :total="bindTotal"
          v-model:page="bindPageNo"
          v-model:limit="bindPageSize"
          @pagination="loadBind"
        />
      </el-tab-pane>

      <el-tab-pane label="提现审核" name="withdraw">
        <el-alert
          class="mb-12px"
          type="warning"
          :closable="false"
          title="通过仅为台账确认出账，≠ 已打到用户银行卡/微信。"
        />
        <div class="mb-12px flex flex-wrap gap-12px items-center">
          <el-select v-model="withdrawStatus" class="!w-140px" @change="onWithdrawStatusChange">
            <el-option label="待审" value="pending" />
            <el-option label="已通过" value="approved" />
            <el-option label="已驳回" value="rejected" />
          </el-select>
          <el-button type="primary" @click="loadWithdraw">刷新</el-button>
        </div>
        <el-table v-loading="withdrawLoading" :data="withdrawList" size="small" border>
          <el-table-column prop="withdrawId" label="工单" min-width="140" />
          <el-table-column prop="phone" label="手机号" min-width="120" />
          <el-table-column prop="amount" label="金额" width="90" />
          <el-table-column prop="cardLast4" label="尾号" width="80" />
          <el-table-column prop="realName" label="实名" min-width="100" />
          <el-table-column label="状态" width="100" align="center">
            <template #default="{ row }">
              <el-tag :type="withdrawStatusTagType(row.status)" size="small">
                {{ withdrawStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="申请时间" min-width="170">
            <template #default="{ row }">{{ formatCapacityDateTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column prop="rejectReason" label="驳回原因" min-width="140" />
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <template v-if="row.status === 'pending'">
                <el-button link type="success" @click="onWithdrawReview(row, true)">通过</el-button>
                <el-button link type="danger" @click="onWithdrawReject(row)">驳回</el-button>
              </template>
              <span v-else class="text-gray-400">—</span>
            </template>
          </el-table-column>
        </el-table>
        <Pagination
          :total="withdrawTotal"
          v-model:page="withdrawPageNo"
          v-model:limit="withdrawPageSize"
          @pagination="loadWithdraw"
        />
      </el-tab-pane>

      <el-tab-pane label="钱包查询" name="query">
        <div class="mb-12px flex flex-wrap gap-12px items-center">
          <el-input v-model="queryPhone" placeholder="骑手手机号" class="!w-200px" clearable />
          <el-button type="primary" @click="loadQuery">查询</el-button>
        </div>
        <template v-if="queryWallet">
          <el-descriptions :column="2" border size="small" class="mb-16px">
            <el-descriptions-item label="手机">{{ queryWallet.phone }}</el-descriptions-item>
            <el-descriptions-item label="可提现">¥{{ queryWallet.available }}</el-descriptions-item>
            <el-descriptions-item label="冻结中">¥{{ queryWallet.frozen ?? 0 }}</el-descriptions-item>
            <el-descriptions-item label="欠款">¥{{ queryWallet.debt ?? 0 }}</el-descriptions-item>
            <el-descriptions-item label="绑卡状态">
              {{ cardStatusLabel(queryWallet.cardStatus) }}
            </el-descriptions-item>
            <el-descriptions-item label="冻提现">
              {{ queryWallet.withdrawFrozen ? '是' : '否' }}
            </el-descriptions-item>
            <el-descriptions-item label="生效卡">
              <span v-if="queryWallet.cardLast4">
                {{ queryWallet.realName }} · 尾号 {{ queryWallet.cardLast4 }}
              </span>
              <span v-else>—</span>
            </el-descriptions-item>
            <el-descriptions-item label="待审卡">
              <span v-if="queryWallet.pendingCardLast4">
                {{ queryWallet.pendingRealName }} · 尾号 {{ queryWallet.pendingCardLast4 }}
              </span>
              <span v-else>—</span>
            </el-descriptions-item>
            <el-descriptions-item v-if="queryWallet.cardRejectReason" label="绑卡驳回" :span="2">
              {{ queryWallet.cardRejectReason }}
            </el-descriptions-item>
          </el-descriptions>
          <el-table :data="queryLedger" size="small" border>
            <el-table-column label="类型" width="120">
              <template #default="{ row }">{{ ledgerTypeLabel(row.type) }}</template>
            </el-table-column>
            <el-table-column prop="amount" label="金额" width="100" />
            <el-table-column prop="remark" label="说明" min-width="200" />
            <el-table-column label="时间" min-width="170">
              <template #default="{ row }">{{ formatCapacityDateTime(row.createdAt) }}</template>
            </el-table-column>
          </el-table>
        </template>
        <el-empty v-else description="输入手机号查询钱包与流水" />
      </el-tab-pane>
    </el-tabs>
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as Api from '@/api/capacity/wallet'
import { formatCapacityDateTime } from '@/api/capacity/labels'
import { ElMessage, ElMessageBox } from 'element-plus'

defineOptions({ name: 'CapacityWallet' })

const cardStatusLabel = (s?: string) =>
  ({ none: '未绑卡', pending: '待审核', approved: '已通过', rejected: '已驳回' } as Record<string, string>)[
    s || ''
  ] || s || '—'

const cardStatusTagType = (s?: string) =>
  ({ none: 'info', pending: 'warning', approved: 'success', rejected: 'danger' } as Record<
    string,
    'info' | 'warning' | 'success' | 'danger'
  >)[s || ''] || 'info'

const withdrawStatusLabel = (s?: string) =>
  ({ pending: '待审核', approved: '已通过', rejected: '已驳回' } as Record<string, string>)[
    s || ''
  ] || s || '—'

const withdrawStatusTagType = (s?: string) =>
  ({ pending: 'warning', approved: 'success', rejected: 'danger' } as Record<
    string,
    'warning' | 'success' | 'danger'
  >)[s || ''] || 'info'

const ledgerTypeLabel = (t?: string) =>
  ({
    rider_credit: '完单收入',
    withdraw: '提现',
    fine: '扣款',
    fine_debt: '欠款',
    fine_refund: '处罚退回',
    empty_run: '空跑补贴'
  } as Record<string, string>)[t || ''] || t || '—'

const tab = ref('bind')

const bindList = ref<Api.WalletView[]>([])
const bindLoading = ref(false)
const bindPageNo = ref(1)
const bindPageSize = ref(10)
const bindTotal = ref(0)

const withdrawList = ref<Api.WithdrawRecord[]>([])
const withdrawLoading = ref(false)
const withdrawStatus = ref('pending')
const withdrawPageNo = ref(1)
const withdrawPageSize = ref(10)
const withdrawTotal = ref(0)

const queryPhone = ref('')
const queryWallet = ref<Api.WalletView>()
const queryLedger = ref<Api.LedgerEntry[]>([])

const loadBind = async () => {
  bindLoading.value = true
  try {
    const r = await Api.listPendingBindCards({
      pageNo: bindPageNo.value,
      pageSize: bindPageSize.value
    })
    bindList.value = r?.list || []
    bindTotal.value = r?.total || 0
  } finally {
    bindLoading.value = false
  }
}

const onBindReview = async (row: Api.WalletView, approved: boolean) => {
  const r = await Api.reviewBindCard({ phone: row.phone, approved })
  if (!r.success) return ElMessage.error(r.message || '失败')
  ElMessage.success(r.message || '已通过')
  await loadBind()
}

const onBindReject = async (row: Api.WalletView) => {
  const { value } = await ElMessageBox.prompt('请填写驳回原因（骑手可见）', '驳回绑卡', {
    confirmButtonText: '驳回',
    cancelButtonText: '取消',
    inputPattern: /\S+/,
    inputErrorMessage: '原因不能为空'
  })
  const r = await Api.reviewBindCard({ phone: row.phone, approved: false, reason: value })
  if (!r.success) return ElMessage.error(r.message || '失败')
  ElMessage.success(r.message || '已驳回')
  await loadBind()
}

const loadWithdraw = async () => {
  withdrawLoading.value = true
  try {
    const r = await Api.listWithdraws({
      status: withdrawStatus.value,
      pageNo: withdrawPageNo.value,
      pageSize: withdrawPageSize.value
    })
    withdrawList.value = r?.list || []
    withdrawTotal.value = r?.total || 0
  } finally {
    withdrawLoading.value = false
  }
}

const onWithdrawStatusChange = () => {
  withdrawPageNo.value = 1
  loadWithdraw()
}

const onWithdrawReview = async (row: Api.WithdrawRecord, approved: boolean) => {
  const r = await Api.reviewWithdraw({ withdrawId: row.withdrawId, approved })
  if (!r.success) return ElMessage.error(r.message || '失败')
  ElMessage.success(r.message || '已通过')
  await loadWithdraw()
}

const onWithdrawReject = async (row: Api.WithdrawRecord) => {
  const { value } = await ElMessageBox.prompt('请填写驳回原因（骑手可见）', '驳回提现', {
    confirmButtonText: '驳回',
    cancelButtonText: '取消',
    inputPattern: /\S+/,
    inputErrorMessage: '原因不能为空'
  })
  const r = await Api.reviewWithdraw({
    withdrawId: row.withdrawId,
    approved: false,
    reason: value
  })
  if (!r.success) return ElMessage.error(r.message || '失败')
  ElMessage.success(r.message || '已驳回')
  await loadWithdraw()
}

const loadQuery = async () => {
  const phone = queryPhone.value?.trim()
  if (!phone) return ElMessage.warning('请输入手机号')
  queryWallet.value = await Api.getWallet(phone)
  queryLedger.value = (await Api.getLedger(phone)) || []
}

watch(tab, (v) => {
  if (v === 'bind') loadBind()
  if (v === 'withdraw') loadWithdraw()
})

onMounted(loadBind)
</script>
